/**
 * A dictionary of skills, so matching a resume to a job needs no AI: "ReactJS", "React.js" and
 * "react" are one skill, and a job that asks for "React" finds the resume that says "ReactJS".
 *
 * Each entry is `Canonical|alias|alias`. Matching is on whole words (so "Java" never matches inside
 * "JavaScript", and "Go" is not taken from the word "go"); a handful of ambiguous short names are
 * written with a context word ("Go (language)" is matched by "golang" only). The list is a reasonable
 * start across tech, data, design, marketing, sales, finance, HR and operations: adding a skill is one
 * line, and nothing else changes.
 */

const RAW: readonly string[] = [
  // Languages
  'JavaScript|javascript|ecmascript|es6',
  'TypeScript|typescript',
  'Python|python|python3',
  'Java|java|core java|j2ee|java ee',
  'C++|c++|cpp',
  'C#|c#|csharp',
  'C|c programming|c language',
  'Go|golang',
  'Rust|rust lang|rust programming',
  'PHP|php',
  'Ruby|ruby',
  'Swift|swift programming|swiftui',
  'Kotlin|kotlin',
  'Scala|scala',
  'R|r programming|r language',
  'MATLAB|matlab',
  'Dart|dart',
  'Perl|perl',
  'Shell Scripting|shell scripting|bash|bash scripting|shell script',
  'SQL|sql',
  'HTML|html|html5',
  'CSS|css|css3',
  // Web / frameworks
  'React|react|reactjs|react.js',
  'React Native|react native',
  'Angular|angular|angularjs|angular.js',
  'Vue|vue|vuejs|vue.js',
  'Next.js|next.js|nextjs',
  'Node.js|node.js|nodejs|node',
  'Express|express.js|expressjs',
  'NestJS|nestjs|nest.js',
  'Django|django',
  'Flask|flask',
  'FastAPI|fastapi',
  'Spring Boot|spring boot|springboot',
  'Spring|spring framework',
  'Hibernate|hibernate',
  '.NET|.net|dotnet|asp.net|.net core',
  'Laravel|laravel',
  'Ruby on Rails|ruby on rails|rails',
  'Flutter|flutter',
  'Android|android development|android sdk',
  'iOS|ios development|ios',
  'Tailwind CSS|tailwind|tailwindcss',
  'Redux|redux',
  'GraphQL|graphql',
  'REST APIs|rest api|rest apis|restful|restful api|restful apis',
  'Microservices|microservices|micro services',
  'jQuery|jquery',
  'Bootstrap|bootstrap',
  'Webpack|webpack',
  // Data / AI
  'Machine Learning|machine learning|ml',
  'Deep Learning|deep learning',
  'NLP|nlp|natural language processing',
  'Computer Vision|computer vision',
  'TensorFlow|tensorflow',
  'PyTorch|pytorch',
  'scikit-learn|scikit-learn|sklearn|scikit learn',
  'Pandas|pandas',
  'NumPy|numpy',
  'Data Analysis|data analysis|data analytics',
  'Data Science|data science',
  'Data Engineering|data engineering',
  'Data Visualization|data visualization|data visualisation',
  'Statistics|statistics|statistical analysis',
  'Power BI|power bi|powerbi',
  'Tableau|tableau',
  'Excel|excel|ms excel|microsoft excel|advanced excel',
  'Apache Spark|apache spark|spark|pyspark',
  'Hadoop|hadoop',
  'Kafka|kafka|apache kafka',
  'Airflow|airflow|apache airflow',
  'ETL|etl',
  'Data Warehousing|data warehousing|data warehouse',
  'Snowflake|snowflake',
  'BigQuery|bigquery',
  'LLM|llm|large language models',
  'Generative AI|generative ai|genai|gen ai',
  // Databases
  'PostgreSQL|postgresql|postgres',
  'MySQL|mysql',
  'MongoDB|mongodb|mongo',
  'Redis|redis',
  'Oracle|oracle db|oracle database',
  'SQL Server|sql server|mssql|ms sql',
  'Elasticsearch|elasticsearch|elastic search',
  'DynamoDB|dynamodb',
  'Cassandra|cassandra',
  'SQLite|sqlite',
  'Firebase|firebase',
  // Cloud / DevOps
  'AWS|aws|amazon web services',
  'Azure|azure|microsoft azure',
  'Google Cloud|google cloud|gcp',
  'Docker|docker',
  'Kubernetes|kubernetes|k8s',
  'Terraform|terraform',
  'Ansible|ansible',
  'Jenkins|jenkins',
  'CI/CD|ci/cd|cicd|ci cd|continuous integration',
  'GitHub Actions|github actions',
  'Git|git',
  'GitHub|github',
  'GitLab|gitlab',
  'Linux|linux|unix',
  'Nginx|nginx',
  'DevOps|devops',
  'Prometheus|prometheus',
  'Grafana|grafana',
  'Serverless|serverless|aws lambda|lambda',
  // QA / process
  'Manual Testing|manual testing',
  'Test Automation|test automation|automation testing',
  'Selenium|selenium',
  'Cypress|cypress',
  'Playwright|playwright',
  'Appium|appium',
  'Puppeteer|puppeteer',
  'TestNG|testng',
  'Jest|jest',
  'JUnit|junit',
  'Postman|postman',
  'JIRA|jira',
  'Agile|agile|scrum|kanban',
  'System Design|system design',
  'Data Structures|data structures|dsa',
  'Algorithms|algorithms',
  'OOP|oop|object oriented programming|object-oriented programming',
  'Cybersecurity|cybersecurity|cyber security|information security|infosec',
  'Networking|networking|computer networks',
  'Blockchain|blockchain',
  // Product / design
  'Product Management|product management|product manager',
  'Roadmapping|roadmapping|product roadmap',
  'UX Design|ux design|user experience',
  'UI Design|ui design|user interface design',
  'Figma|figma',
  'Adobe Photoshop|photoshop|adobe photoshop',
  'Adobe Illustrator|illustrator|adobe illustrator',
  'Adobe XD|adobe xd',
  'Wireframing|wireframing|wireframes',
  'User Research|user research',
  'Prototyping|prototyping',
  'Video Editing|video editing|premiere pro|after effects',
  'Graphic Design|graphic design',
  // Marketing / sales
  'Digital Marketing|digital marketing',
  'SEO|seo|search engine optimization|search engine optimisation',
  'SEM|sem|search engine marketing',
  'Google Ads|google ads|adwords',
  'Facebook Ads|facebook ads|meta ads',
  'Google Analytics|google analytics',
  'Social Media Marketing|social media marketing|smm',
  'Content Marketing|content marketing',
  'Content Writing|content writing|copywriting',
  'Email Marketing|email marketing',
  'Marketing Automation|marketing automation|hubspot',
  'Brand Management|brand management',
  'Market Research|market research',
  'Lead Generation|lead generation',
  'Sales|sales',
  'B2B Sales|b2b sales',
  'Inside Sales|inside sales',
  'Business Development|business development',
  'Account Management|account management|key account management',
  'CRM|crm',
  'Salesforce|salesforce',
  'Negotiation|negotiation|negotiation skills',
  'Cold Calling|cold calling',
  'Customer Success|customer success',
  'Customer Support|customer support|customer service',
  // Finance / accounting
  'Accounting|accounting',
  'Financial Reporting|financial reporting',
  'Financial Analysis|financial analysis|financial modelling|financial modeling',
  'Budgeting|budgeting|forecasting',
  'Accounts Payable|accounts payable',
  'Accounts Receivable|accounts receivable',
  'Taxation|taxation|income tax',
  'GST|gst',
  'TDS|tds',
  'Tally|tally|tally erp',
  'SAP|sap|sap fico|sap erp',
  'Auditing|auditing|internal audit|statutory audit',
  'Payroll|payroll|payroll processing',
  'Reconciliation|reconciliation|bank reconciliation',
  'IFRS|ifrs',
  // HR
  'Recruitment|recruitment|talent acquisition|recruiting',
  'Sourcing|sourcing|candidate sourcing',
  'Onboarding|onboarding',
  'Employee Engagement|employee engagement',
  'Performance Management|performance management',
  'HRIS|hris|hrms',
  'Compensation and Benefits|compensation and benefits|compensation & benefits|c&b',
  'Employee Relations|employee relations',
  'Labour Law|labour law|labor law',
  'Learning and Development|learning and development|l&d|learning & development',
  'Training|training|corporate training',
  // Ops / management
  'Project Management|project management|project manager',
  'Program Management|program management',
  'Operations Management|operations management',
  'Supply Chain|supply chain|supply chain management',
  'Logistics|logistics',
  'Procurement|procurement|purchasing',
  'Vendor Management|vendor management',
  'Inventory Management|inventory management',
  'Quality Assurance|quality assurance|qa',
  'Process Improvement|process improvement|lean|six sigma',
  'Stakeholder Management|stakeholder management',
  'Team Management|team management|team leadership|people management',
  'Leadership|leadership',
  'Communication|communication|communication skills',
  'Problem Solving|problem solving|problem-solving',
  'Time Management|time management',
  'MS Office|ms office|microsoft office|office 365',
  'PowerPoint|powerpoint|ms powerpoint',
  'Business Analysis|business analysis|business analyst',
  'Requirements Gathering|requirements gathering',
  'Data Entry|data entry',
  'Documentation|documentation|technical documentation',
  'Public Speaking|public speaking|presentation skills',
  'Compliance|compliance',
  'Risk Management|risk management',
  'Legal Research|legal research',
  'Contract Drafting|contract drafting|contract management',
  // Domain tooling
  'AutoCAD|autocad',
  'SolidWorks|solidworks',
  'PLC|plc',
  'Embedded Systems|embedded systems|embedded c',
  'IoT|iot|internet of things',
  'VLSI|vlsi',
  'Healthcare|healthcare',
  'Pharmacovigilance|pharmacovigilance',
  'Teaching|teaching|classroom management',
];

export interface SkillEntry {
  readonly name: string;
  readonly aliases: readonly string[];
}

/** Skills whose name is also an everyday word or a single letter: only their longer aliases match. */
const NAME_IS_AMBIGUOUS: ReadonlySet<string> = new Set([
  'Go', 'R', 'C', 'Swift', 'Rust', 'Spring', 'Express', 'Oracle', 'Training',
]);

export const SKILLS: readonly SkillEntry[] = RAW.map((line) => {
  const [name, ...aliases] = line.split('|');
  const own = NAME_IS_AMBIGUOUS.has(name!) ? [] : [name!.toLowerCase()];
  // The first alias of an ambiguous entry repeats its name ("go"), so it is dropped too.
  const rest = NAME_IS_AMBIGUOUS.has(name!) ? aliases.filter((a) => a !== name!.toLowerCase()) : aliases;
  return { name: name!, aliases: [...new Set([...own, ...rest])] };
});

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

/**
 * One pattern per alias. A skill name is found only as a whole word: not preceded by a letter or digit,
 * and not followed by one (or by `+`/`#`, so `C` is not found in `C++`). A trailing full stop at the end
 * of a sentence is fine; `node.js` is matched as written.
 */
const PATTERNS: ReadonlyArray<{ name: string; re: RegExp }> = SKILLS.flatMap((skill) =>
  skill.aliases.map((alias) => ({
    name: skill.name,
    re: new RegExp(`(?<![a-z0-9])${escape(alias)}(?![a-z0-9+#])`, 'i'),
  })),
);

/** Skills named in some text, canonical names, each once, in the order they first appear. */
export function findSkills(text: string): string[] {
  if (!text) return [];
  const found = new Map<string, number>();
  for (const { name, re } of PATTERNS) {
    const match = re.exec(text);
    if (match && (!found.has(name) || match.index < found.get(name)!)) found.set(name, match.index);
  }
  return [...found.entries()].sort((a, b) => a[1] - b[1]).map(([name]) => name);
}

const BY_ALIAS = new Map<string, string>();
for (const skill of SKILLS) for (const alias of skill.aliases) if (!BY_ALIAS.has(alias)) BY_ALIAS.set(alias, skill.name);

/** The canonical name for a tag a person typed ("ReactJS" -> "React"); unknown tags are kept as typed. */
export function canonicalSkill(tag: string): string {
  const key = tag.trim().toLowerCase();
  return BY_ALIAS.get(key) ?? tag.trim();
}
